import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  Card, 
  Tag, 
  Typography,
  message,
  Popconfirm,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { publicationService } from '../../services/publicationService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Publication, Attachment, PublicationCategory } from '../../types';
import { formatLocalizedDateTime, formatSimpleDateTime } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

// 테이블 스타일 정의
const tableStyles = `
  .publication-table .ant-table-tbody > tr > td:nth-child(3) {
    text-align: left !important;
  }
  
  .publication-table .ant-table-thead > tr > th:nth-child(3) {
    text-align: left !important;
  }
  
  .publication-table .ant-table-tbody > tr > td:nth-child(3) .ant-btn {
    text-align: left !important;
    justify-content: flex-start !important;
    display: flex !important;
  }
  
  .publication-table .ant-table-tbody > tr > td:nth-child(3) .ant-btn > span {
    text-align: left !important;
    width: 100% !important;
  }
`;

const PublicationList: React.FC = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [filteredPublications, setFilteredPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();



  // 카테고리 번역 함수
  const getCategoryTranslation = (category: PublicationCategory | string): string => {
    switch (category) {
      case PublicationCategory.PUBLICATION:
      case '공포':
        return t('publications.categories.PUBLICATION');
      case PublicationCategory.ANNOUNCEMENT:
      case '공시':
        return t('publications.categories.ANNOUNCEMENT');
      default:
        return category;
    }
  };

  const fetchPublications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await publicationService.getAllPublications();
      setPublications(data);
      setFilteredPublications(data);
    } catch (error) {
      message.error(t('publications.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // 검색 기능
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredPublications(publications);
      return;
    }
    
    const filtered = publications.filter(publication =>
      publication.title.toLowerCase().includes(value.toLowerCase()) ||
      publication.category.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPublications(filtered);
  };

  useEffect(() => {
    fetchPublications();
  }, [fetchPublications]);

  const handleDelete = async (publicationId: string) => {
    try {
      await publicationService.deletePublication(publicationId);
      message.success(t('publications.deleteSuccess'));
      fetchPublications();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('publications.deleteFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      message.error(t('publications.downloadFailed'));
    }
  };

  const columns = [
    {
      title: t('publications.number'),
      dataIndex: 'publication_id',
      key: 'publication_id',
      width: 50,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => filteredPublications.length - index,
    },
    {
      title: t('publications.category'),
      dataIndex: 'category',
      key: 'category',
      width: 70,
      align: 'center' as const,
      render: (category: PublicationCategory) => (
        <Tag color={category === PublicationCategory.PUBLICATION ? 'blue' : 'green'} style={{ fontSize: '12px', padding: '2px 6px' }}>
          {getCategoryTranslation(category)}
        </Tag>
      ),
    },
    {
      title: t('publications.title'),
      dataIndex: 'title',
      key: 'title',
      width: isAdmin ? 300 : 380, // 승인/승인취소 버튼이 제거되어 제목 너비 복원
      ellipsis: false,
      align: 'left' as const,
      render: (text: string, record: Publication) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/publications/${record.publication_id}`)}
          style={{ 
            padding: 0, 
            height: 'auto', 
            textAlign: 'left',
            width: '100%',
            overflow: 'visible',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            lineHeight: '1.3',
            fontSize: '14px',
            justifyContent: 'flex-start',
            display: 'flex'
          }}
          title={text}
        >
          {text}
        </Button>
      ),
    },
    {
      title: t('publications.attachments'),
      dataIndex: 'attachments',
      key: 'attachments',
      width: 60,
      align: 'center' as const,
      render: (attachments: Attachment[]) => {
        if (!attachments || attachments.length === 0) {
          return null;
        }
        
        return (
          <Space size="small">
            {attachments.slice(0, 3).map((attachment) => (
              <Button
                key={attachment.attachment_id}
                type="text"
                size="small"
                icon={<span style={{ fontSize: '16px' }}>📎</span>}
                onClick={() => handleDownloadAttachment(attachment)}
                style={{ 
                  padding: '1px 3px', 
                  minWidth: 'auto',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={decodeURIComponent(attachment.file_name)}
              />
            ))}
            {attachments.length > 3 && (
              <span style={{ fontSize: '12px', color: '#666' }}>
                +{attachments.length - 3}
              </span>
            )}
          </Space>
        );
      },
    },
    {
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}>
          <span>{t('publications.createdAt')}</span>
          <span style={{ fontSize: '11px', color: '#666' }}>({t('publications.updatedAt')})</span>
        </div>
      ),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (date: string, record: Publication) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
          <span title={formatLocalizedDateTime(date, i18n.language)} style={{ fontSize: '12px' }}>
            {formatSimpleDateTime(date)}
          </span>
          {record.updated_at && record.updated_at !== record.created_at && (
            <span title={formatLocalizedDateTime(record.updated_at, i18n.language)} style={{ fontSize: '11px', color: '#666' }}>
              ({formatSimpleDateTime(record.updated_at)})
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('publications.viewCount'),
      dataIndex: 'view_count',
      key: 'view_count',
      width: 60,
      align: 'center' as const,
      render: (count: number) => count.toLocaleString(),
    },
    // 관리자만 볼 수 있는 승인 상태 컬럼
    ...(isAdmin ? [
      {
        title: t('publications.approvalStatus'),
        dataIndex: 'is_approved',
        key: 'is_approved',
        width: 80,
        align: 'center' as const,
        render: (isApproved: boolean) => (
          <Tag color={isApproved ? 'green' : 'orange'}>
            {isApproved ? t('publications.approved') : t('publications.pending')}
          </Tag>
        ),
      },
    ] : []),
    // 관리자만 볼 수 있는 컬럼들
    ...(isAdmin ? [
      {
        title: i18n.language === 'ko' ? '작업' : 'Actions',
        key: 'actions',
        width: 80,
        align: 'center' as const,
        render: (_: any, record: Publication) => (
          <Space size="small">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/publications/${record.publication_id}`)}
              style={{ padding: '1px 3px', minWidth: 'auto' }}
            />
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/publications/${record.publication_id}/edit`)}
              disabled={record.is_approved}
              style={{ 
                padding: '1px 3px', 
                minWidth: 'auto',
                opacity: record.is_approved ? 0.5 : 1
              }}
              title={record.is_approved ? t('publications.cannotEditApproved') : t('common.edit')}
            />

            <Popconfirm
              title={record.is_approved ? t('publications.cannotDeleteApproved') : t('publications.confirmDelete')}
              onConfirm={() => !record.is_approved && handleDelete(record.publication_id)}
              okText={t('common.delete')}
              cancelText={t('common.cancel')}
              disabled={record.is_approved}
            >
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                disabled={record.is_approved}
                style={{ 
                  padding: '1px 3px', 
                  minWidth: 'auto',
                  opacity: record.is_approved ? 0.5 : 1
                }}
                title={record.is_approved ? t('publications.cannotDeleteApproved') : t('common.delete')}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ] : []),
  ];

  return (
    <div style={{ padding: '16px' }}>
      <style>{tableStyles}</style>
      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('publications.title')}
          </Title>
          <Space>
            <Input.Search
              placeholder={t('publications.searchPlaceholder')}
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              value={searchText}
            />
            {isAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/publications/create')}
              >
                {t('publications.create')}
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredPublications}
          rowKey="publication_id"
          loading={loading}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${t('common.total')} ${total}`,
          }}
          scroll={{ x: 800 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          className="publication-table"
        />
      </Card>
    </div>
  );
};

export default PublicationList; 